import { always, assocPath, cond, equals, fromPairs, mapObjIndexed, pipe, T } from 'ramda';
import * as structure from '../../shared/interfaces';
import { clientDb } from '../firebase';
import { emptyObjConst, makeAudit } from '../helpers';
import { ItemType, UserAuth } from '../interfaces';
import { thrower } from '../ramda';
import { ExtraInfoEntry, ExtraInfoFields, iDeviceDetails, iExtraInfo, ReportFilterSectionName, TagEditFieldName } from './../interfaces';
import { NoteFieldKind } from './note-extra-fields-db';

const labelDb = () => clientDb().child('extra-info');

// export const all = async (): Promise<structure.iList<structure.iExtraInfo>> => (await labelDb().once("value")).val();

export const allWatch = (callback) => labelDb().on('value', pipe(
    (res) => {
        const values: structure.iList<iExtraInfo> = res.val();
        const uniqueValues = [];

        Object.entries(values).forEach(([key, item]) => {
            const filtered = uniqueValues.filter(([key, label]) => item && label.details.name === item?.details.name);
            filtered.length === 0 && uniqueValues.push([key, item]);
        });

//        uniqueValues.forEach(([key, item]) => {
//            if (item?.details?.name === 'Make') {
//                item.details.name = 'Vehicle';
//            }
//        });

        return fromPairs(uniqueValues) || emptyObjConst
    },
    mapObjIndexed((obj, idx) => assocPath(['details', 'id'], idx, obj)),
    callback
), (err) => console.log(err));

export const update = (user: UserAuth) => async (key, info: { [key: string]: any }) => {
    await clientDb().update(makeAudit(user, {
        [`extra-info/${key}`]: { details: info },
    }));
};

export const deleteLabels = (user: UserAuth) => async (ids) => {
    const update = {};

    ids.forEach((id) => {
        update[`extra-info/${id}`] = null;
    });

    await clientDb().update(makeAudit(user, update));
};

/**
 * Update a itemId - val null for removal
 */
export const updateItemInfoValue = (user: UserAuth) => async (type: ItemType, itemId: string, labels: structure.iList<string>) => {
    const path = cond([
        [equals(ItemType.device), always(`devices/device-details/${itemId}/extra-info/`)],
        [equals(ItemType.fence), always(`fences/fence-instances/${itemId}/extra-info`)],
        [equals(ItemType.person), always(`people/people-details/${itemId}/extra-info`)],
        [equals(ItemType.tag), always(`tags-extra-info-values/${itemId}`)],
        [T, thrower('missing item type handler')]
    ])(type);

    clientDb().update(makeAudit(user, {
        [path]: labels,
    }));
};

export const getOrganizationExtraInfo = async () => {
    const extraInfoSnapshot = await clientDb().child('/extra-info').once('value');
    const extraInfo: Record<string, ExtraInfoEntry> = extraInfoSnapshot.val() || {};

    const mappedExtraInfo: Record<string, string> = {};

    for (const [key, value] of Object.entries(extraInfo)) {
        const name = value.details?.name || '';

        if (Object.values(ExtraInfoFields).includes(name as ExtraInfoFields)) {
            mappedExtraInfo[name] = key;
        }
    }

    return mappedExtraInfo;
};

export const getDeviceDetails = async (deviceId: string) => {
    const value: iDeviceDetails = (await clientDb().child(`/devices/device-details/${deviceId}`).once('value')).val();

    return value;
}

export const setDeviceColor = async (
    user: UserAuth,
    deviceIds: string | string[],
    value: string
) => {
    const ids = Array.isArray(deviceIds) ? deviceIds : [deviceIds];

    const updates: Record<string, any> = {};
    for (const id of ids) {
        updates[`/devices/device-details/${id}/color`] = value;
    }

    await clientDb().update(makeAudit(user, updates));
};

export const getDeviceExtraInfo = async (deviceId: string, key: string) => {
    const value = (await clientDb().child(`/devices/device-details/${deviceId}/extra-info/${key}`).once('value')).val();

    return value;
}

export const setHideExtraInfo = async (user: UserAuth, value: boolean, key: string) => {
    await clientDb().update(makeAudit(user, {
        [`default-settings/visibility-settings/extra-info-visibility/${key}`]: value
    }));
}

export const getHideExtraInfoValue = async (key: string) => {
    const value = (await clientDb().child(`default-settings/visibility-settings/extra-info-visibility/${key}`).once('value')).val();

    return value;
}

export const setPinVisibilityValue = async (user: UserAuth, value: boolean) => {
    await clientDb().update(makeAudit(user, {
        [`default-settings/visibility-settings/pin`]: value
    }));
}

export const getPinVisibility = async () => {
    const value = (await clientDb().child(`default-settings/visibility-settings/pin`).once('value')).val();

    return value;
}

export const setTravelReportFieldVisibility = async (user: UserAuth, value: boolean, key: string) => {
    await clientDb().update(makeAudit(user, {
        [`default-settings/visibility-settings/travel-report-field-visibility/${key}`]: value
    }));
}

export const getTravelReportFieldVisibility = async () => {
    const value = (await clientDb().child(`default-settings/visibility-settings/travel-report-field-visibility`).once('value')).val();

    return value;
}

export const setReportFilterVisibility = async (user: UserAuth, filter: ReportFilterSectionName, value: boolean) => {
    await clientDb().update(makeAudit(user, {
        [`default-settings/visibility-settings/report-filter/${filter}`]: value
    }));
}

type ReportFilterVisibility = Record<ReportFilterSectionName, boolean>;

export const getReportFilterVisibility = async (): Promise<ReportFilterVisibility> => {
    const value = (await clientDb().child(`default-settings/visibility-settings/report-filter`).once('value')).val();

    return value;
}

export const setTagEditFieldVisibility = async (user: UserAuth, field: TagEditFieldName, value: boolean) => {
    await clientDb().update(makeAudit(user, {
        [`default-settings/visibility-settings/tag-edit-fields/${field}`]: value
    }));
}

export const getTagEditFieldVisibility = async () => {
    const value = (await clientDb().child(`default-settings/visibility-settings/tag-edit-fields`).once('value')).val();

    return value;
}

const VALID_LABEL_KINDS: NoteFieldKind[] = ['text', 'number', 'money', 'boolean', 'textarea', 'date', 'select', 'file'];

export const normalizeLabelKind = (kind: unknown): NoteFieldKind => {
  if (typeof kind === 'string' && VALID_LABEL_KINDS.includes(kind as NoteFieldKind)) {
    return kind as NoteFieldKind;
  }
  return 'text';
};

export const updateLabelDetails = async (
  authUser: UserAuth,
  labelId: string,
  name: string,
  kind: NoteFieldKind,
): Promise<void> => {
  await clientDb().update(
    makeAudit(authUser, {
      [`extra-info/${labelId}/details/name`]: name,
      [`extra-info/${labelId}/details/kind`]: kind,
    }),
  );
};

export const deleteLabelFromDb = async (authUser: UserAuth, labelId: string): Promise<void> => {
  await clientDb().update(makeAudit(authUser, { [`extra-info/${labelId}`]: null }));
};